import { supabase, isSupabaseConfigured } from '../lib/supabase.ts';
export { isSupabaseConfigured };

export type AuthState =
  | 'UNAUTHENTICATED'
  | 'SIGNUP_PENDING'
  | 'EMAIL_VERIFICATION_PENDING'
  | 'EMAIL_VERIFIED'
  | 'AUTHENTICATED'
  | 'SIGNED_OUT';

export interface SupabaseAuthResult {
  success: boolean;
  user?: any;
  session?: any;
  error?: string;
  isFallback?: boolean;
  requiresVerification?: boolean;
  unverified?: boolean;
  authState?: AuthState;
}

export interface TermsConsentMeta {
  consentAgreed: boolean;
  termsVersion: string;
  privacyVersion: string;
  acceptedAt: string;
}

/**
 * Returns true if the user record has a confirmed email timestamp.
 */
export function isUserEmailConfirmed(user: any): boolean {
  if (!user) return false;
  return Boolean(user.email_confirmed_at || user.confirmed_at);
}

/**
 * Determines current authentication state using Supabase session.
 */
export async function getCurrentAuthState(): Promise<AuthState> {
  if (!isSupabaseConfigured()) {
    const token = localStorage.getItem('qb_auth_token');
    return token ? 'AUTHENTICATED' : 'UNAUTHENTICATED';
  }
  try {
    const res = await supabase.auth.getSession();
    const session = res?.data?.session ?? null;
    if (!session || !session.user) {
      return 'UNAUTHENTICATED';
    }
    if (isUserEmailConfirmed(session.user)) {
      return 'AUTHENTICATED';
    }
    return 'EMAIL_VERIFICATION_PENDING';
  } catch {
    return 'UNAUTHENTICATED';
  }
}

/**
 * Sign up a new user using Supabase Auth (email & password).
 * Enforces email verification gate (returns EMAIL_VERIFICATION_PENDING).
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
  username: string,
  country: string = 'PH',
  consentMeta?: TermsConsentMeta
): Promise<SupabaseAuthResult> {
  const cleanEmail = email.trim().toLowerCase();

  // Try API route first to ensure server-side user/profile validation
  try {
    const apiRes = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        password,
        displayName,
        username,
        country,
        termsAcceptedAt: consentMeta?.acceptedAt || new Date().toISOString(),
        termsVersion: consentMeta?.termsVersion || '2026.1',
        privacyVersion: consentMeta?.privacyVersion || '2026.1',
      }),
    });
    const apiData = await apiRes.json();
    if (!apiRes.ok) {
      return {
        success: false,
        error: apiData?.error || 'Registration failed.',
        authState: 'UNAUTHENTICATED',
      };
    }
    return {
      success: true,
      user: apiData?.user,
      requiresVerification: true,
      authState: 'EMAIL_VERIFICATION_PENDING',
    };
  } catch (apiErr: any) {
    // If server signup route is unavailable, fallback to client Supabase SDK
    if (!isSupabaseConfigured()) {
      return {
        success: false,
        error: apiErr.message || 'Supabase authentication is not configured.',
        isFallback: true,
        authState: 'UNAUTHENTICATED',
      };
    }

    try {
      const signUpRes = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          data: {
            display_name: displayName,
            username,
            country,
            terms_accepted_at: consentMeta?.acceptedAt || new Date().toISOString(),
            terms_version: consentMeta?.termsVersion || '2026.1',
            privacy_version: consentMeta?.privacyVersion || '2026.1',
          },
        },
      });

      if (signUpRes?.error) {
        return {
          success: false,
          error: signUpRes.error.message,
          authState: 'UNAUTHENTICATED',
        };
      }

      const user = signUpRes?.data?.user ?? null;
      const session = signUpRes?.data?.session ?? null;
      const confirmed = isUserEmailConfirmed(user);
      return {
        success: true,
        user,
        session,
        requiresVerification: !confirmed,
        authState: confirmed ? 'AUTHENTICATED' : 'EMAIL_VERIFICATION_PENDING',
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Supabase sign up failed',
        authState: 'UNAUTHENTICATED',
      };
    }
  }
}

/**
 * Sign in existing user via Supabase Auth.
 * Enforces email verification gate (rejects unverified accounts).
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<SupabaseAuthResult> {
  const cleanEmail = email.trim().toLowerCase();

  // Try API route first for server-side auth checking & email verification gate
  try {
    const apiRes = await fetch('/api/auth/signin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, password }),
    });
    const apiData = await apiRes.json();
    if (!apiRes.ok) {
      if (apiData?.requiresVerification || apiData?.unverified) {
        return {
          success: false,
          error:
            apiData?.error ||
            'Your email address is not verified yet. Please enter your 6-digit code.',
          requiresVerification: true,
          unverified: true,
          authState: 'EMAIL_VERIFICATION_PENDING',
        };
      }
      return {
        success: false,
        error: apiData?.error || 'Invalid email or password.',
        authState: 'UNAUTHENTICATED',
      };
    }

    return {
      success: true,
      user: apiData?.account?.user,
      session: apiData?.session ?? null,
      authState: 'AUTHENTICATED',
    };
  } catch (apiErr: any) {
    if (!isSupabaseConfigured()) {
      return {
        success: false,
        error: apiErr.message || 'Supabase authentication is not configured.',
        isFallback: true,
        authState: 'UNAUTHENTICATED',
      };
    }

    try {
      const signInRes = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });

      if (signInRes?.error) {
        return {
          success: false,
          error: signInRes.error.message,
          authState: 'UNAUTHENTICATED',
        };
      }

      const user = signInRes?.data?.user ?? null;
      const session = signInRes?.data?.session ?? null;

      if (!isUserEmailConfirmed(user)) {
        // Sign out client session immediately to prevent unverified bypass
        await supabase.auth.signOut().catch(() => {});
        // Request fresh OTP
        await supabase.auth.resend({ type: 'signup', email: cleanEmail }).catch(() => {});
        return {
          success: false,
          error:
            'Your email address is not verified yet. Please enter the 6-digit verification code.',
          requiresVerification: true,
          unverified: true,
          authState: 'EMAIL_VERIFICATION_PENDING',
        };
      }

      return {
        success: true,
        user,
        session,
        authState: 'AUTHENTICATED',
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Supabase sign in failed',
        authState: 'UNAUTHENTICATED',
      };
    }
  }
}

/**
 * Verify 6-digit OTP code using Supabase verifyOtp and server sync, with null-safe session recovery.
 */
export async function verifyEmailOtp(
  email: string,
  otpCode: string,
  password?: string
): Promise<{
  success: boolean;
  error?: string;
  session?: any;
  account?: any;
  authState: AuthState;
}> {
  const cleanEmail = email.trim().toLowerCase();
  const cleanOtp = otpCode.trim();

  if (!cleanOtp || cleanOtp.length !== 6 || !/^\d{6}$/.test(cleanOtp)) {
    return {
      success: false,
      error: 'Please enter all 6 numeric digits of your verification code.',
      authState: 'EMAIL_VERIFICATION_PENDING',
    };
  }

  let verifiedSession: any = null;
  let verifiedAccount: any = null;
  let verifiedUser: any = null;
  let lastErrorMsg: string | null = null;

  // Step 1: Try server endpoint /api/auth/verify-otp (which calls verifyOtp & syncs DB profile)
  try {
    const res = await fetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        otpCode: cleanOtp,
        type: 'signup',
      }),
    });
    const data = await res.json().catch(() => null);
    console.log('[OTP Verify] /api/auth/verify-otp result:', {
      status: res.status,
      success: data?.success,
      hasSession: Boolean(data?.session),
      hasAccount: Boolean(data?.account),
      error: data?.error,
    });

    if (!res.ok || data?.error) {
      lastErrorMsg = data?.error || 'Invalid or expired verification code.';
    } else if (data?.success) {
      verifiedSession = data?.session ?? null;
      verifiedAccount = data?.account ?? null;
      verifiedUser =
        verifiedSession?.user ||
        (verifiedAccount?.user
          ? {
              id: verifiedAccount.user.uid,
              email: verifiedAccount.user.email || cleanEmail,
              email_confirmed_at: new Date().toISOString(),
              user_metadata: {
                username: verifiedAccount.user.username,
                display_name: verifiedAccount.user.displayName,
                country: verifiedAccount.user.country,
                avatar_url: verifiedAccount.user.avatarUrl,
              },
            }
          : null);
    }
  } catch (serverErr: any) {
    console.warn('[OTP Verify] Server verify-otp request failed, trying client SDK:', serverErr?.message);
  }

  // Step 2: If server didn't return success/session, try direct supabase.auth.verifyOtp({ email, token, type: 'signup' })
  if (!verifiedSession && !verifiedAccount && isSupabaseConfigured()) {
    try {
      let verifyRes = await supabase.auth.verifyOtp({
        email: cleanEmail,
        token: cleanOtp,
        type: 'signup',
      });

      if (verifyRes?.error) {
        const altRes = await supabase.auth.verifyOtp({
          email: cleanEmail,
          token: cleanOtp,
          type: 'email',
        });
        if (!altRes?.error) {
          verifyRes = altRes;
        }
      }

      console.log('[OTP Verify] Client supabase.auth.verifyOtp result:', {
        hasError: Boolean(verifyRes?.error),
        error: verifyRes?.error?.message,
        hasSession: Boolean(verifyRes?.data?.session),
        hasUser: Boolean(verifyRes?.data?.user),
      });

      if (verifyRes?.error) {
        return {
          success: false,
          error:
            verifyRes.error.message ||
            lastErrorMsg ||
            'The verification code is invalid or has expired.',
          authState: 'EMAIL_VERIFICATION_PENDING',
        };
      }

      verifiedSession = verifyRes?.data?.session ?? null;
      verifiedUser = verifyRes?.data?.user ?? verifiedSession?.user ?? null;
      if (verifiedUser && !verifiedAccount) {
        verifiedAccount = {
          user: {
            uid: verifiedUser.id,
            email: verifiedUser.email || cleanEmail,
            username:
              verifiedUser.user_metadata?.username ||
              cleanEmail.split('@')[0] ||
              'scholar',
            displayName:
              verifiedUser.user_metadata?.display_name ||
              verifiedUser.user_metadata?.username ||
              cleanEmail.split('@')[0] ||
              'Scholar',
            country: verifiedUser.user_metadata?.country || 'PH',
            avatarUrl: verifiedUser.user_metadata?.avatar_url || '',
            role: 'scholar',
            xp: 0,
            level: 1,
          },
        };
      }
    } catch (err: any) {
      console.error('[OTP Verify] Client verifyOtp error:', err);
      if (!verifiedAccount) {
        return {
          success: false,
          error: err.message || lastErrorMsg || 'OTP verification failed.',
          authState: 'EMAIL_VERIFICATION_PENDING',
        };
      }
    }
  }

  // If neither server nor client succeeded and we have an error, return it immediately
  if (!verifiedSession && !verifiedAccount && !verifiedUser) {
    return {
      success: false,
      error:
        lastErrorMsg ||
        'The verification code is invalid or has expired. Please check your email or request a new code.',
      authState: 'EMAIL_VERIFICATION_PENDING',
    };
  }

  // Hydrate client Supabase session if we got tokens from server
  if (verifiedSession?.access_token && verifiedSession?.refresh_token && isSupabaseConfigured()) {
    try {
      const setRes = await supabase.auth.setSession({
        access_token: verifiedSession.access_token,
        refresh_token: verifiedSession.refresh_token,
      });
      if (setRes?.data?.session) {
        verifiedSession = setRes.data.session;
      }
    } catch (err: any) {
      console.warn('[OTP Verify] setSession warning:', err?.message);
    }
  }

  // Step 3: If session is still null, call supabase.auth.getSession() and use that
  if (!verifiedSession && isSupabaseConfigured()) {
    try {
      const getSessionRes = await supabase.auth.getSession();
      verifiedSession = getSessionRes?.data?.session ?? null;
      console.log(
        '[OTP Verify] Fallback supabase.auth.getSession() result:',
        Boolean(verifiedSession)
      );
    } catch (err: any) {
      console.warn('[OTP Verify] getSession fallback error:', err?.message);
    }
  }

  // Step 4: If there is still no session and password was provided, call signInWithPassword({ email, password })
  if (!verifiedSession && password) {
    console.log(
      '[OTP Verify] Session still null after getSession(); attempting signInWithPassword fallback...'
    );
    if (isSupabaseConfigured()) {
      try {
        const signInRes = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        });
        if (!signInRes?.error && signInRes?.data?.session) {
          verifiedSession = signInRes.data.session;
          verifiedUser = verifiedUser || signInRes.data.user;
          console.log('[OTP Verify] Client signInWithPassword fallback succeeded.');
        }
      } catch (err: any) {
        console.warn('[OTP Verify] Client signInWithPassword fallback error:', err?.message);
      }
    }

    if (!verifiedSession) {
      try {
        const apiSignIn = await fetch('/api/auth/signin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cleanEmail, password }),
        });
        const apiSignInData = await apiSignIn.json().catch(() => null);
        if (apiSignIn.ok && apiSignInData?.session) {
          verifiedSession = apiSignInData.session;
          verifiedAccount = verifiedAccount || apiSignInData.account;
          console.log('[OTP Verify] Server /api/auth/signin fallback succeeded.');
          if (
            verifiedSession?.access_token &&
            verifiedSession?.refresh_token &&
            isSupabaseConfigured()
          ) {
            await supabase.auth
              .setSession({
                access_token: verifiedSession.access_token,
                refresh_token: verifiedSession.refresh_token,
              })
              .catch(() => {});
          }
        }
      } catch (err: any) {
        console.warn('[OTP Verify] Server signin fallback error:', err?.message);
      }
    }
  }

  // Step 5: Guarantee a structured session object if verification succeeded and we have user/account info
  if (!verifiedSession && (verifiedUser || verifiedAccount?.user)) {
    const u = verifiedUser || {
      id: verifiedAccount.user.uid,
      email: verifiedAccount.user.email || cleanEmail,
      email_confirmed_at: new Date().toISOString(),
      user_metadata: {
        username: verifiedAccount.user.username,
        display_name: verifiedAccount.user.displayName,
        country: verifiedAccount.user.country,
        avatar_url: verifiedAccount.user.avatarUrl,
      },
    };
    verifiedSession = {
      access_token: localStorage.getItem('qb_auth_token') || `verified-${Date.now()}`,
      user: u,
    };
  }

  if (verifiedSession?.access_token) {
    localStorage.setItem('qb_auth_token', verifiedSession.access_token);
  }

  console.log('[OTP Verify] Final session status:', {
    sessionFound: Boolean(verifiedSession),
    email: verifiedSession?.user?.email || verifiedAccount?.user?.email || cleanEmail,
  });

  return {
    success: true,
    session: verifiedSession,
    account: verifiedAccount,
    authState: 'AUTHENTICATED',
  };
}

/**
 * Resend 6-digit OTP code using native Supabase resend API.
 */
export async function resendEmailOtp(
  email: string
): Promise<{ success: boolean; error?: string; message?: string }> {
  const cleanEmail = email.trim().toLowerCase();

  // Try server endpoint first
  try {
    const res = await fetch('/api/auth/resend-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, type: 'signup' }),
    });
    const data = await res.json();
    if (res.ok) {
      return {
        success: true,
        message: data.message || `A new 6-digit code has been sent to ${cleanEmail}.`,
      };
    }
    return {
      success: false,
      error: data.error || 'Failed to resend code.',
    };
  } catch {
    // Try client SDK
    if (isSupabaseConfigured()) {
      try {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: cleanEmail,
        });
        if (error) {
          return { success: false, error: error.message };
        }
        return {
          success: true,
          message: `A new 6-digit code has been sent to ${cleanEmail}.`,
        };
      } catch (err: any) {
        return { success: false, error: err.message || 'Failed to resend code.' };
      }
    }
    return {
      success: false,
      error: 'Unable to resend code. Please try again later.',
    };
  }
}

/**
 * Sign in using Google OAuth via Supabase Auth.
 */
export async function signInWithGoogleOAuth(): Promise<SupabaseAuthResult> {
  if (!isSupabaseConfigured()) {
    return {
      success: false,
      error:
        'Supabase is not configured yet. Please configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
      isFallback: true,
      authState: 'UNAUTHENTICATED',
    };
  }

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      return { success: false, error: error.message, authState: 'UNAUTHENTICATED' };
    }

    return { success: true, user: data, authState: 'AUTHENTICATED' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Google OAuth failed', authState: 'UNAUTHENTICATED' };
  }
}

/**
 * Sign out current Supabase session.
 */
export async function signOutSupabase(): Promise<SupabaseAuthResult> {
  localStorage.removeItem('qb_auth_token');
  if (!isSupabaseConfigured()) {
    return { success: true, isFallback: true, authState: 'SIGNED_OUT' };
  }

  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      return { success: false, error: error.message, authState: 'SIGNED_OUT' };
    }
    return { success: true, authState: 'SIGNED_OUT' };
  } catch (err: any) {
    return { success: false, error: err.message, authState: 'SIGNED_OUT' };
  }
}

/**
 * Send password reset email via Supabase.
 */
export async function resetPasswordSupabase(email: string): Promise<SupabaseAuthResult> {
  if (!isSupabaseConfigured()) {
    return {
      success: false,
      error:
        'Supabase is not configured yet. Please configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment variables.',
      isFallback: true,
    };
  }

  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/#reset-password`,
    });
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Check active Supabase session.
 */
export async function getSupabaseSession() {
  if (!isSupabaseConfigured()) return null;
  try {
    const res = await supabase.auth.getSession();
    return res?.data?.session ?? null;
  } catch {
    return null;
  }
}
